import React from 'react';
import _ from 'lodash';

import { makeStyles, Grid } from '@material-ui/core';

import {
  InstrumentNameCell,
  SRRIColumn,
} from '../../../../../Modelportfolios/components/InstrumentsList/table-data';
import InputFormElement, { CurrencyNumberFormat } from '../../../../../RiskProfiling/components/StepContent/components/formElement/InputFormElement';
import {
  ORDERING_AVAILABLE_OPTIONS_FIELD_NAMES,
  ORDERING_MP_AVAILABLE_OPTIONS_FIELD_NAMES,
  TRANSACTION_INSTR,
  TRANSACTION_INSTR_ORDERING_FIELD_NAMES, TRANSACTION_TYPE_INTEGER,
  TRANSACTION_TYPE_VALUES,
} from '../../../../constants';
import {formatQty, mergeClasses, withEuroOrDash} from '../../../../../../utils/utils';
import {
  getAmount,
  getNumberValue,
  getTransactionItemSRRI, tradeNeedRiskReasoning,
} from '../../../../utils';
import {
  useRiskScoreConfirmationModalContext,
} from '../../../../../RiskProfiling/components/StepContent/components/step/ProductsSelectionSteps/components/InvestmentRecommendationStep/InvestmentRecommendationStep';
import {
  QtyNumberFormat, BrokerFeeColumn as BaseBrokerFeeColumn, VirtualDiscountInput, DiscountTotal, SelectAllCheckbox,
} from '../../../TradeStep/components/PortfolioTrade/table-data';
import SingleBuyBookingDate
  from '../../../../../RiskProfiling/components/StepContent/components/step/ProductSelectionStepsV2/components/SingleBuyBookingDate';
import BcaIconButton, { BcaDeleteIconButton } from '../../../../../../components/Buttons/BcaIconButton/BcaIconButton';
import { WARNING_COLOR } from '../../../../../../themes/themeConfigurator';
import SelectFormElementV2
  from '../../../../../RiskProfiling/components/StepContent/components/formElement/SelectFormElementV2';
import { SustainabilityCell } from '../../../../../../utils/commonTableColumns';
import { MODEL_PORTFOLIO_WEIGHT } from '../../../../../Modelportfolios/Create/constants';
import {
  MPExpandCol,
  MPWeightCol,
  NameColCommon,
  PortfolioNameCol,
  PriceColumn
} from "../shared/modelPortfolioTableColumns";


const TRANSACTION_INSTR_OPTIONS = [
  {id: TRANSACTION_INSTR.amount, value: TRANSACTION_INSTR.amount, label: 'Betrag'},
  {id: TRANSACTION_INSTR.qty, value: TRANSACTION_INSTR.qty, label: 'Stück'}
];

const useCellStyles = makeStyles((theme) => ({
  inputRoot: {
    height: 30,
    margin: 0,
    fontSize: 14,
    width: '100%',
    '&.Mui-error': {
      borderColor: '#f44336',
    },
    '&.text-red': {
      color: '#f44336 !important',
    },
    '&.text-green': {
      color: '#60ce7a !important',
    },
  },
  priceContainer: {
    textAlign: 'right',
    fontSize: 12,
    whiteSpace: 'nowrap',
  },
  nowrap: {
    whiteSpace: 'nowrap',
  },
  reasonBtn: {
    color: WARNING_COLOR,
    position: 'absolute',
    top: -6,
  },
  sellAllCheckboxRoot: {
    margin: 0,
  },
  sellAllCheckboxLabel: {
    fontSize: 12,
    fontFamily: 'Roboto-Regular'
  }
}));

const isNewVirtualAmountDisabled = (options) => {
  return options.isNewVirtual && options.transactionsWeightType != MODEL_PORTFOLIO_WEIGHT.AUTO;
};

export const NameCol = {
  ...NameColCommon,
  header: [({ options }) => InstrumentNameCell.header.content(options) || null],
  body: [({ item, options }) => InstrumentNameCell.body.content(item.data, options) || null],
  colSpan: {footer: 3},
};

export const SustainabilityCol = {
  header: [() => SustainabilityCell.header.content(undefined, { sustainabilityTitle: 'Nachhaltig-keit' })],
  body: [({ item, options }) => SustainabilityCell.body.content(item.data, options)],
  cellClassName: 'sustainability-cell'
};

export const SRRICol = {
  header: [({options}) => options.isModelportfolio ? <>RK/<br/>SRI</> : (SRRIColumn.header || null)],
  body: [({ item, options }) => {
    const classes = useCellStyles();

    const itemSRRI = getTransactionItemSRRI(item, options.isModelportfolio, true);

    // FIXME: for MP and PI: item.mpComponent
    const displayTextFieldButton = tradeNeedRiskReasoning(
      item, options.isModelportfolio, options.isAnlageberatung, options.clientSRRI
    );

    const { onSelectAsset } = useRiskScoreConfirmationModalContext();

    return (
      <>
        {SRRIColumn.body.content({ internal_sri: itemSRRI }, options)}

        {displayTextFieldButton && (
          <span style={{ margin: 0, position: 'relative' }}>
            <BcaIconButton
              tooltip={'Begründung'}
              icon={<i className={'fal fa-comment-alt-exclamation'} />}
              onClick={() => onSelectAsset && onSelectAsset(item)}
              customClasses={{ root: classes.reasonBtn }}
            />
          </span>
        )}
      </>
    );
  }],
};

const TransactionTypeSelection = React.memo(({ item, options }) => {
  const classes = useCellStyles();

  const quotationFieldsSource = options.isModelportfolio
    ? ORDERING_MP_AVAILABLE_OPTIONS_FIELD_NAMES
    : ORDERING_AVAILABLE_OPTIONS_FIELD_NAMES;

  const radioOptions = TRANSACTION_INSTR_OPTIONS.map((option) => {
    const isDisabled = !_.get(options, quotationFieldsSource[options.action], [])
      .includes(TRANSACTION_INSTR_ORDERING_FIELD_NAMES[option.id]);
    return { ...option, disabled: isDisabled };
  });

  return (
    <SelectFormElementV2
      disabled={item.loading || item.sell_all || false}
      onChange={value => options.handleTransactionTypeChange && options.handleTransactionTypeChange(item, value)}
      value={item.transaction_type || ''}
      options={radioOptions}
      custom_classes={{inputRoot: classes.inputRoot}}
      error={_.get(item, 'errors.transaction_type')}
    />
  );
});

const AmountInput = React.memo(({ item, options, disabled=false }) => {
  const classes = useCellStyles();

  const isAmount = item.transaction_type === TRANSACTION_INSTR.amount;
  const inputComponent = isAmount ? CurrencyNumberFormat : QtyNumberFormat;

  const inputProps = React.useMemo(() => {
    const props = { allowNegative: false, fixedDecimalScale: isAmount };
    if (!isAmount) {
      props.decimalScale = options.maxQtyDecimals;
      if (!item.data.is_fund) {
        props.decimalSeparator = false;
      }
    }
    return props;
  }, [isAmount, options.maxQtyDecimals, item.data.is_fund]);

  const decimals = isAmount ? undefined : false;

  const transactionValueError = _.get(item, 'errors.transaction_value');
  const assetPriceError = _.get(item, 'errors.asset_price');
  const error = (transactionValueError || assetPriceError)
    ? <>{transactionValueError}{transactionValueError && assetPriceError && <br />}{assetPriceError}</>
    : options.hasError;

  return (
    <InputFormElement
      disabled={item.loading || isNewVirtualAmountDisabled(options) || disabled}
      error={error}
      value={getNumberValue(item.transaction_value, decimals)}
      onChange={value => options.handleTransactionValueChange && options.handleTransactionValueChange(item, value)}
      inputComponent={inputComponent}
      inputProps={{ inputProps: inputProps }}
      type={'text'}
      custom_classes={classes}
    />
  );
});

const showPriceInfo = (options, item) => !options.isModelportfolio && (item.transaction_type === TRANSACTION_INSTR.qty)

const TransactionTypeColumn = {
  header: [() => 'Einheit'],
  body: [({ item, options }) => {
    const classes = useCellStyles();
    const isShowPriceInfo = showPriceInfo(options, item)

    return (
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <TransactionTypeSelection item={item} options={options} />
        </Grid>
        {isShowPriceInfo && (
          <Grid item xs={12} className={classes.priceContainer}>
            <b>Preis:</b> {withEuroOrDash(item.data.price_eur)}
          </Grid>
        )}
      </Grid>
    )
  }],
  footer: [() => 'Gesamt'],
  cellClassName: 'with-input',
  align: 'right',
}

const AmountColumnBody = ({options, item, handleBookingDateChange, amountDisabled}) => {
  const classes = useCellStyles();

  const isShowPriceInfo = showPriceInfo(options, item)

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <AmountInput item={item} options={options} disabled={amountDisabled} />
      </Grid>
      {isShowPriceInfo && (
        <Grid item xs={12}>
          <div className={classes.priceContainer}><b>Gesamt:</b> {withEuroOrDash(getAmount(item))}</div>
        </Grid>
      )}
      {!!handleBookingDateChange && (
        <Grid item xs={12}>
          <SingleBuyBookingDate
            item={item}
            onChange={handleBookingDateChange}
            hasMultipleOrders={options.hasMultipleOrders}
          />
        </Grid>
      )}
    </Grid>
  );
}

const getTotalAmount = (items) => {
  return (items || []).reduce((sum, item) => {
    const isAmount = item.transaction_type === TRANSACTION_INSTR.amount;
    return sum + (isAmount ? (getNumberValue(item.transaction_value) || 0) : (getAmount(item) || 0));
  }, 0);
}

const BuyAmountColumn = {
  header: [({ options }) => {
    return options.isVirtual ? 'Bruttobetrag' : <span>Menge /<br />Betrag, Ausführung</span>;
  }],
  body: [({ item, options }) => {
    if (item.mpComponent) {
      return withEuroOrDash(item.allocated_amount);
    }
    return <AmountColumnBody item={item} options={options} handleBookingDateChange={options.handleBookingDateChange} />;
  }],
  footer: [({ items }) => {
    const total = getTotalAmount(items.filter(i => !i.mpComponent))

    return (
      <b>{withEuroOrDash(total)}</b>
    );
  }],
  align: 'right',
  cellClassName: 'with-input amount-cell',
};

export const DiscountColumn = {
  header: [({ options }) => options.isVirtual ? 'Rabatt' : 'Anmerkung'],
  body: [({ item, options }) => {
    if (item.mpComponent) return null;

    const classes = useCellStyles();

    if (options.isVirtual) {
      return <VirtualDiscountInput item={item} options={options} classes={classes} />;
    }

    return (
      <DiscountTotal
        item={item}
        options={options}
        onChange={(value) => options.handleDiscountChange && options.handleDiscountChange(item, value)}
        custom_classes={classes}
      />
    );
  }],
  footer: [() => null],
  align: 'right',
  cellClassName: 'with-input discount-cell',
};

export const DeleteActionColumn = {
  header: [
    ({ options }) => {
      if(!options.onRemoveAllClick) return null;

      return (
        <BcaDeleteIconButton color={'secondary'} tooltip={"Alles löschen"} onClick={() => options.onRemoveAllClick()} />
      );
    },
  ],
  body: [({ item, options }) => {
    if (item.mpComponent) return null;
    if(!options.handleRemoveClick) return null;

    return <BcaDeleteIconButton color={'secondary'} onClick={() => options.handleRemoveClick(item)} />
  }],
  footer: [() => null],
  align: 'center',
  cellClassName: 'with-input delete-cell',
};


const SellAmountColumn = {
  header: [() => <span>Menge /<br />Betrag</span>],
  body: [({item, options}) => {
    if (item.mpComponent) {
      return withEuroOrDash(item.allocated_amount);
    }
    return <AmountColumnBody item={item} options={options} amountDisabled={item.sell_all} />;
  }],
  footer: [({ items }) => {
    const total = -getTotalAmount(items.filter(i => !i.mpComponent))

    return (
      <b>{withEuroOrDash(total)}</b>
    );
  }],
  align: 'right',
  cellClassName: 'with-input amount-cell',
};

export const SellAllBody = ({item, options, customClasses, fieldToCheck, fieldToCompare}) => {
  const classes = useCellStyles();

  return (
    <SelectAllCheckbox
      classes={mergeClasses(
        {root: classes.sellAllCheckboxRoot, label: classes.sellAllCheckboxLabel},
        customClasses
      )}
      item={item}
      options={options}
      label={'Alles'}
      fieldToCheck={fieldToCheck}
      fieldToCompare={fieldToCompare}
    />
  )
}

const SellActualValueColumn = {
  header: [() => 'Aktueller Wert'],
  body: [({item, options}) => {
    const classes = useCellStyles();

    if(item.mpComponent) return null;

    let isAmount = item.transaction_type === TRANSACTION_INSTR.amount;

    return (
      <>
        <div className={classes.nowrap}>
          {isAmount
            ? withEuroOrDash(item.data.market_value)
            : `${formatQty(item.data.quantity, options.maxQtyDecimals)} STÜCK`
          }
        </div>
        <SellAllBody item={item} options={options} />
      </>
    )

  }],
  footer: [() => null],
  align: 'right',
}

export const BrokerFeeColumn = {
  ...BaseBrokerFeeColumn,
  body: [({ item, options }) => {
    if (item.mpComponent) return null;
    return BaseBrokerFeeColumn.body[0]({ item, options });
  }],
  footer: [({ items, options }) => {
    const filtered = (items || []).filter(i => !i.mpComponent);
    return BaseBrokerFeeColumn.footer[0]({ items: filtered, options });
  }],
};

const PortfolioNameTradeCol = {
  ...PortfolioNameCol,
  colSpan: {footer: 4}
}

const MPSellAmountColumn = {...SellAmountColumn, header: [() => 'Betrag']}
const MPBuyAmountColumn = {...BuyAmountColumn, header: [() => <>Betrag<br />Ausführung</>]}


export const getBuySellTableStructure = () => {
  return {
    real: {
      [TRANSACTION_TYPE_VALUES.BUY]: [
        NameCol,
        SustainabilityCol,
        SRRICol,
        TransactionTypeColumn,
        BuyAmountColumn,
        BrokerFeeColumn,
        DiscountColumn,
        DeleteActionColumn,
      ],
      [TRANSACTION_TYPE_VALUES.SELL]: [
        NameCol,
        SustainabilityCol,
        SRRICol,
        TransactionTypeColumn,
        SellAmountColumn,
        SellActualValueColumn,
        DeleteActionColumn
      ],
    },
    model_portfolio: {
      [TRANSACTION_TYPE_VALUES.BUY]: [
        MPExpandCol,
        PortfolioNameTradeCol,
        SustainabilityCol,
        SRRICol,
        PriceColumn,
        MPWeightCol,
        MPBuyAmountColumn,
        BrokerFeeColumn,
        DiscountColumn,
        DeleteActionColumn
      ],
      [TRANSACTION_TYPE_VALUES.SELL]: [
        MPExpandCol,
        PortfolioNameTradeCol,
        SustainabilityCol,
        SRRICol,
        PriceColumn,
        MPWeightCol,
        MPSellAmountColumn,
        SellActualValueColumn,
        DeleteActionColumn
      ]
    }
  };
};